import React, { useState, useEffect, useRef } from 'react';
import { CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon } from '../../Icons';

const DAYS_MAP: Record<number, string> = {
    1: "LUNES", 2: "MARTES", 3: "MIERCOLES", 4: "JUEVES", 5: "VIERNES", 6: "SABADO", 0: "DOMINGO"
};

interface CustomCalendarProps {
    value: string;
    onChange: (val: string) => void;
    minDate: string;
    visitConfig: any;
}

export const CustomCalendar: React.FC<CustomCalendarProps> = ({ value, onChange, minDate, visitConfig }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [viewDate, setViewDate] = useState(new Date());
    const containerRef = useRef<HTMLDivElement>(null);

    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const monthName = viewDate.toLocaleString('es-ES', { month: 'long' });

    const isAvailable = (d: number) => {
        const date = new Date(year, month, d);
        const dateStr = date.toISOString().split('T')[0];
        const dayName = DAYS_MAP[date.getDay()];
        if (dateStr < minDate) return false;
        return !!visitConfig?.HORARIO?.[dayName];
    };

    const handleSelect = (d: number) => {
        if (!isAvailable(d)) return;
        const date = new Date(year, month, d);
        onChange(date.toISOString().split('T')[0]);
        setIsOpen(false);
    };

    useEffect(() => {
        const clickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setIsOpen(false);
        };
        document.addEventListener('mousedown', clickOutside);
        return () => document.removeEventListener('mousedown', clickOutside);
    }, []);

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();

    return (
        <div className="relative" ref={containerRef}>
            <button type="button" onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md text-sm bg-white hover:border-sky-500 transition-colors">
                <span className={value ? 'text-gray-900 font-medium' : 'text-gray-400'}>{value ? value.split('-').reverse().join('/') : 'Selecciona fecha'}</span>
                <CalendarDaysIcon className="w-5 h-5 text-gray-400" />
            </button>
            {isOpen && (
                <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-xl shadow-2xl z-[1001] p-4 animate-fade-in-up">
                    <div className="flex justify-between items-center mb-4 text-gray-800">
                        <h4 className="text-sm font-black uppercase tracking-tighter">{monthName} {year}</h4>
                        <div className="flex space-x-1">
                            <button type="button" onClick={() => setViewDate(new Date(year, month - 1, 1))} className="p-1 hover:bg-gray-100 rounded-md"><ChevronLeftIcon className="w-4 h-4" /></button>
                            <button type="button" onClick={() => setViewDate(new Date(year, month + 1, 1))} className="p-1 hover:bg-gray-100 rounded-md"><ChevronRightIcon className="w-4 h-4" /></button>
                        </div>
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-center mb-1">
                        {['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'].map(d => <span key={d} className="text-[10px] font-black text-gray-400 uppercase">{d}</span>)}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                        {Array.from({ length: firstDay }).map((_, i) => <div key={i} />)}
                        {Array.from({ length: daysInMonth }).map((_, i) => {
                            const d = i + 1;
                            const av = isAvailable(d);
                            const sel = value === `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                            return (
                                <button key={d} type="button" disabled={!av} onClick={() => handleSelect(d)} className={`h-8 w-8 text-xs font-bold rounded-lg transition-all ${sel ? 'bg-sky-600 text-white shadow-md' : (av ? 'text-gray-700 hover:bg-sky-50' : 'text-gray-200 cursor-not-allowed')}`}>{d}</button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};